import { memo, useMemo } from 'react';
import cn from 'classnames';
import { Avatar, Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { CHAT_MODAL_WIDTH_CLASS } from '../../config/constants';
import { buildForwardMessageText } from '../../lib/messageQuote';
import type { ChatMessage, ChatThread } from '../../model/types';
import { IconCheckCircle, IconForward, IconSearch } from '../icons';

function getMessagePreview(message: ChatMessage | null) {
  if (!message) return '';
  return buildForwardMessageText(message) ?? 'Message';
}

export type ForwardMessageModalProps = {
  isOpen: boolean;
  message: ChatMessage | null;
  threads: ChatThread[];
  search: string;
  selectedChatIds: number[];
  isSubmitting: boolean;
  errorMessage: string | null;
  onSearchChange: (value: string) => void;
  onToggleChat: (chatId: number) => void;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
};

export const ForwardMessageModal = memo(
  ({
    isOpen,
    message,
    threads,
    search,
    selectedChatIds,
    isSubmitting,
    errorMessage,
    onSearchChange,
    onToggleChat,
    onClose,
    onSubmit,
  }: ForwardMessageModalProps) => {
    useModalAutoFocus(isOpen);

    const selectedCount = selectedChatIds.length;
    const subtitle = useMemo(() => {
      if (selectedCount === 0) return 'Choose one or more chats';
      if (selectedCount === 1) return '1 chat selected';
      return `${selectedCount} chats selected`;
    }, [selectedCount]);

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        placement="center"
        backdrop="opaque"
        hideCloseButton={isSubmitting}
        classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] p-0`,
          closeButton: 'top-7 right-7 text-black/50 hover:bg-transparent hover:text-black',
        }}
      >
        <ModalContent className="overflow-hidden p-0">
          <ModalHeader className="flex flex-col gap-2 px-9 pb-0 pt-8">
            <span className="text-[28px] font-semibold text-[#1E1E1E]">Forward message</span>
            <span className="text-sm font-medium text-grey-secondary">{subtitle}</span>
          </ModalHeader>
          <ModalBody className="px-9 pb-0 pt-7">
            <div className="flex flex-col gap-6">
              {message ? (
                <div className="rounded-[18px] bg-[#F6F6F6] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.06em] text-grey-secondary">
                    Forwarding
                  </p>
                  <p className="mt-2 line-clamp-4 whitespace-pre-wrap break-words text-sm leading-5 text-[#1E1E1E]">
                    {getMessagePreview(message)}
                  </p>
                </div>
              ) : null}

              <div className="flex items-center gap-3 rounded-2xl bg-[#F6F6F6] px-4 py-4">
                <IconSearch className="shrink-0 text-grey-secondary" />
                <input
                  value={search}
                  onChange={(event) => onSearchChange(event.target.value)}
                  aria-label="Search chats"
                  placeholder="Search chat"
                  className="w-full bg-transparent text-base text-[#1E1E1E] outline-none placeholder:text-grey"
                />
              </div>

              <div className="max-h-[420px] overflow-y-auto pr-2">
                <ul className="flex flex-col gap-3">
                  {threads.map((thread) => {
                    const isSelected = selectedChatIds.includes(thread.id);

                    return (
                      <li key={thread.id}>
                        <button
                          type="button"
                          onClick={() => onToggleChat(thread.id)}
                          className={cn(
                            'flex w-full items-center gap-4 rounded-3xl px-4 py-3 text-left transition-colors',
                            isSelected ? 'bg-[#F1F1F1]' : 'bg-white hover:bg-[#F7F7F7]',
                          )}
                        >
                          <Avatar
                            name={thread.title}
                            src={thread.avatarUrl}
                            size="lg"
                            showFallback
                            className="h-12 w-12 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[16px] font-medium text-[#1E1E1E]">
                              {thread.title}
                            </p>
                            <p className="mt-0.5 truncate text-sm text-grey-secondary">
                              {thread.subtitle || thread.lastMessagePreview}
                            </p>
                          </div>
                          <div
                            className={cn(
                              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',
                              isSelected
                                ? 'border-[#2FC35B] bg-[#EFFFF3] text-green-tertiary'
                                : 'border-[#D8D8D8] bg-white text-transparent',
                            )}
                          >
                            <IconCheckCircle className="h-4 w-4" />
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {!threads.length ? (
                  <div className="py-12 text-center text-sm text-default-500">No chats found</div>
                ) : null}
              </div>

              {errorMessage ? (
                <p className="rounded-[16px] bg-[#FFF1F1] px-4 py-3 text-sm font-medium text-red-tertiary">
                  {errorMessage}
                </p>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter className="mt-8 flex justify-between gap-5 border-t border-[#ECECEC] px-9 py-6">
            <Button
              variant="bordered"
              className="h-[52px] min-w-[230px] rounded-[12px] border-[#1E1E1E] bg-white text-[16px] font-medium text-[#1E1E1E]"
              onPress={onClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className="h-[52px] min-w-[230px] rounded-[12px] bg-[#27245F] text-[16px] font-medium text-white"
              startContent={<IconForward />}
              isDisabled={!selectedCount || isSubmitting}
              isLoading={isSubmitting}
              onPress={onSubmit}
            >
              Forward
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);

ForwardMessageModal.displayName = 'ForwardMessageModal';
